import React, { useState, useEffect } from 'react';
import { motion } from "motion/react";
import { ChevronRight, Code2, Bot, Cpu, Rocket, Award, Users, Lightbulb, Brain, Target, Zap, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";


export function Home() {
  const images = [
    '/Whisk_boysinclass.jpeg',
    '/Whisk_roboticswithmindstorms.jpeg',
    '/Whisk_15yoboy.jpeg'
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 sm:pt-32 sm:pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-[#F0F9FF] to-white overflow-hidden">
        {/* Animated Background Blobs */}
        <motion.div 
          animate={{ scale: [1, 1.2, 1], x: [0, 50, 0], y: [0, -30, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          className="absolute top-20 right-10 w-96 h-96 bg-[#00FFFF]/15 rounded-full blur-[100px] pointer-events-none"
        />
        <motion.div 
          animate={{ scale: [1, 1.3, 1], x: [0, -40, 0], y: [0, 60, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-20 left-10 w-[500px] h-[500px] bg-[#FFD700]/10 rounded-full blur-[120px] pointer-events-none"
        />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-start">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00FFFF]/20 to-[#FFD700]/20 text-[#2E5C8A] px-4 py-2 rounded-full mb-8 font-semibold shadow-sm border border-[#5B9BD5]/10">
                <Sparkles className="w-4 h-4 text-[#FFD700]" />
                Empowering Young Innovators
              </div> */}
              <h1 className="text-4xl sm:text-5xl md:text-7xl text-[#0A1A2F] mb-4 leading-[1.1] tracking-tight">
                Code the Future, <br />
                <span className="bg-gradient-to-r from-[#2E5C8A] via-[#5B9BD5] to-[#00FFFF] bg-clip-text text-transparent">
                  Build Tomorrow
                </span>
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-8 sm:mb-10 leading-relaxed max-w-xl">
                Inspiring students through hands-on coding and robotics.
                From beginner to advanced, we turn learners into creators.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-5">
                <Button asChild className="bg-gradient-to-r from-[#2E5C8A] to-[#5B9BD5] hover:opacity-95 text-white h-14 sm:h-16 px-8 sm:px-10 rounded-full text-base sm:text-lg font-bold shadow-xl shadow-blue-200 border-none transition-all hover:scale-105 active:scale-95 group">
                  <Link to="/programs" className="flex items-center justify-center gap-2">
                    Get Started <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
                <Button variant="outline" asChild className="border-2 border-[#FFD700] hover:bg-[#FFD700]/10 text-gray-900 h-14 sm:h-16 px-8 sm:px-10 rounded-full text-base sm:text-lg font-bold transition-all hover:scale-105 active:scale-95">
                  <Link to="/about" className="flex items-center justify-center">View Programs</Link>
                </Button>
              </div>

              {/* Stats Banner */}
              <div className="mt-12 sm:mt-16 grid grid-cols-3 sm:grid-cols-5 gap-4 sm:gap-6">
                 <div>
                  <div className="text-3xl sm:text-4xl font-extrabold text-[#5B9BD5]">3</div>
                  <div className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-widest mt-1">Counties</div>
                </div>
                <div>
                  <div className="text-3xl sm:text-4xl font-extrabold text-[#2E5C8A]">4+</div>
                  <div className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-widest mt-1">Schools</div>
                </div>
                <div>
                  <div className="text-3xl sm:text-4xl font-extrabold text-[#00D4D4]">405+</div>
                  <div className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-widest mt-1">Students</div>
                </div>
               
                {/* <div>
                  <div className="text-3xl sm:text-4xl font-extrabold text-[#00D4D4]">3+</div>
                  <div className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-widest mt-1">Partners</div>
                </div> */}
                <div>
                  <div className="text-3xl sm:text-4xl font-extrabold text-[#FFD700]">95%</div>
                  <div className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-widest mt-1">Rating</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="relative"
            >
              <div className="relative z-10 bg-white p-4 rounded-[40px] shadow-2xl overflow-hidden border border-gray-100">
                <div className="aspect-[4/3] rounded-[30px] overflow-hidden relative">
                  <ImageWithFallback 
                    src={images[currentIndex]} 
                    alt="Child coding" 
                    className="w-full h-full object-cover transition-opacity duration-500"
                    style={{ transform: 'translateY(-50px)' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2E5C8A]/60 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
                    <div className="text-white">
                      <p className="text-sm font-semibold opacity-80 mb-1">Live Workshop</p>
                      <h3 className="text-2xl font-bold leading-tight">Interactive Python <br />for Teens</h3>
                    </div>
                    <div className="bg-white/20 backdrop-blur-md p-3 rounded-2xl border border-white/30">
                      <Zap className="w-8 h-8 text-[#FFD700]" />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-8 -right-8 w-20 h-20 bg-[#00FFFF] rounded-2xl flex items-center justify-center shadow-2xl rotate-12 z-20 border-4 border-white"
              >
                <Bot className="w-10 h-10 text-[#0A1A2F]" />
              </motion.div>
              <motion.div
                animate={{ y: [0, 15, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                className="absolute -bottom-6 -left-6 w-[72px] h-[72px] bg-[#FFD700] rounded-full flex items-center justify-center shadow-2xl z-20 border-4 border-white"
              >
                <Code2 className="w-9 h-9 text-[#0A1A2F]" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SmartBrains Core Concepts Section */}
      <section className="py-16 md:py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl text-[#0A1A2F] mb-6 tracking-tight">
                 <span className="text-[#2E5C8A]">The Astra STEAM Methodology</span>
              </h2>
              <p className="text-xl text-gray-600">
                We deliver beginner-friendly, practical and project-based lessons that help learners understand technology, build real robots and develop the confidence to solve problems creatively.
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                icon: <Brain className="w-8 h-8" />, 
                title: "Strategic Thinking", 
                desc: "We teach students to approach problems with a structured, step-by-step logic and long-term vision.",
                color: "bg-[#5B9BD5]" 
              },
              { 
                icon: <Lightbulb className="w-8 h-8" />, 
                title: "Mindset for Innovation", 
                desc: "Encouraging a growth mindset where failure is seen as a data point for improvement and creative breakthroughs.",
                color: "bg-[#FFD700]" 
              },
              { 
                icon: <Target className="w-8 h-8" />, 
                title: "Analytical Precision", 
                desc: "Developing the ability to decompose complex tasks into manageable components through computational thinking.",
                color: "bg-[#00FFFF]" 
              },
              { 
                icon: <ShieldCheck className="w-8 h-8" />, 
                title: "Resilient Problem Solving", 
                desc: "Building confidence and persistence to debug code and re-engineer solutions until they work perfectly.",
                color: "bg-[#2E5C8A]" 
              }
            ].map((concept, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group p-8 rounded-[32px] bg-gray-50 hover:bg-white hover:shadow-2xl transition-all border border-transparent hover:border-gray-100"
              >
                <div className={`w-16 h-16 ${concept.color} rounded-2xl flex items-center justify-center mb-6 text-white group-hover:scale-110 transition-transform`}>
                  {concept.icon}
                </div>
                <h3 className="text-2xl font-bold text-[#0A1A2F] mb-4">{concept.title}</h3>
                <p className="text-gray-600 leading-relaxed">{concept.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Programs Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center mb-16 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-4xl md:text-5xl text-[#0A1A2F] mb-6 tracking-tight">
                Our Signature <span className="text-[#5B9BD5]">Programs</span>
              </h2>
              <p className="text-xl text-gray-600">
                Designed for different age groups and skill levels, our programs provide a comprehensive pathway to tech mastery.
              </p>
            </div>
            <Button asChild variant="ghost" className="text-[#2E5C8A] font-bold text-lg hover:bg-transparent hover:text-[#5B9BD5]">
              <Link to="/programs" className="flex items-center gap-2">
                Browse All Programs <ChevronRight className="w-5 h-5" />
              </Link>
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Program 1 */}
            <motion.div
              whileHover={{ y: -10 }}
              className="bg-white rounded-[40px] overflow-hidden shadow-xl shadow-gray-200/50 border border-gray-100 group"
            >
              <div className="h-64 relative overflow-hidden">
                <ImageWithFallback 
                  // src="https://images.unsplash.com/photo-1758598738113-86c0f874c85a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdGVtJTIwZWR1Y2F0aW9uJTIwY2hpbGRyZW4lMjBwbGF5aW5nJTIwd2l0aCUyMGJsb2Nrc3xlbnwxfHx8fDE3NzY5MzMwODd8MA&ixlib=rb-4.1.0&q=80&w=1080" 
                  src="Whisk_scratch_AI.jpeg"
                  alt="Little Coders"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute top-6 left-6 bg-[#FFD700] text-gray-900 font-bold px-4 py-1.5 rounded-full text-sm">
                  Ages 5-8
                </div>
              </div>
              <div className="p-10">
                <div className="flex items-center gap-3 text-[#5B9BD5] font-semibold mb-4 uppercase tracking-widest text-xs">
                  <Code2 className="w-4 h-4" />
                  Visual Coding
                </div>
                <h3 className="text-2xl font-bold text-[#0A1A2F] mb-4">Little Coders Academy</h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Introduction to logical thinking and basics of coding using blocks and visual tools like Scratch.
                </p>
                <Button asChild className="w-full rounded-2xl h-14 bg-gray-50 hover:bg-[#5B9BD5] text-[#2E5C8A] hover:text-white border-none transition-colors">
                  <Link to="/programs">Explore Pathway</Link>
                </Button>
              </div>
            </motion.div>

            {/* Program 2 */}
            <motion.div
              whileHover={{ y: -10 }}
              className="bg-white rounded-[40px] overflow-hidden shadow-xl shadow-gray-200/50 border border-gray-100 group"
            >
              <div className="h-64 relative overflow-hidden">
                <ImageWithFallback 
                  // src="https://images.unsplash.com/photo-1603354351149-e97b9124020d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyb2JvdGljcyUyMGtpdCUyMGZvciUyMGNoaWxkcmVufGVufDF8fHx8MTc3NjkzMzA4N3ww&ixlib=rb-4.1.0&q=80&w=1080" 
                  src="Whisk_boy_official.png"
                  alt="Robotics Masters"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute top-6 left-6 bg-[#00FFFF] text-gray-900 font-bold px-4 py-1.5 rounded-full text-sm">
                  Ages 9-14
                </div>
              </div>
              <div className="p-10">
                <div className="flex items-center gap-3 text-[#00D4D4] font-semibold mb-4 uppercase tracking-widest text-xs">
                  <Bot className="w-4 h-4" />
                  Hardware & Logic
                </div>
                <h3 className="text-2xl font-bold text-[#0A1A2F] mb-4">Robotics Masterclass</h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Building and programming real robots. From sensors and gears to autonomous navigation.
                </p>
                <Button asChild className="w-full rounded-2xl h-14 bg-gray-50 hover:bg-[#00D4D4] text-[#0A1A2F] hover:text-white border-none transition-colors">
                  <Link to="/programs">Explore Pathway</Link>
                </Button>
              </div>
            </motion.div>

            {/* Program 3 */}
            <motion.div
              whileHover={{ y: -10 }}
              className="bg-white rounded-[40px] overflow-hidden shadow-xl shadow-gray-200/50 border border-gray-100 group"
            >
              {/* <div className="h-64 relative overflow-hidden flex items-center justify-center bg-gradient-to-br from-[#2E5C8A] to-[#5B9BD5]">
                <Cpu className="w-24 h-24 text-white/30 absolute" />
                <div className="z-10 text-center px-10">
                  <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-4 border border-white/20">
                    <Rocket className="w-10 h-10 text-white" />
                  </div>
                  <h4 className="text-white font-bold text-xl uppercase tracking-widest">Innovation Lab</h4>
                </div>
                <div className="absolute top-6 left-6 bg-white text-[#2E5C8A] font-bold px-4 py-1.5 rounded-full text-sm">
                  Ages 15+
                </div>
              </div> */}
              <div className="h-64 relative overflow-hidden">
                <ImageWithFallback 
                  // src="https://images.unsplash.com/photo-1603354351149-e97b9124020d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyb2JvdGljcyUyMGtpdCUyMGZvciUyMGNoaWxkcmVufGVufDF8fHx8MTc3NjkzMzA4N3ww&ixlib=rb-4.1.0&q=80&w=1080" 
                  src="Whisk_teensinclasscoding.jpeg"
                  alt="Robotics Masters"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute top-6 left-6 bg-[#00FFFF] text-gray-900 font-bold px-4 py-1.5 rounded-full text-sm">
                  Ages 15+
                </div>
              </div>
              <div className="p-10">
                <div className="flex items-center gap-3 text-[#FFD700] font-semibold mb-4 uppercase tracking-widest text-xs">
                  <Cpu className="w-4 h-4" />
                  Full-Stack Development
                </div>
                <h3 className="text-2xl font-bold text-[#0A1A2F] mb-4">Future Innovators</h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Advanced software engineering, AI integration, and real-world project development for university prep.
                </p>
                <Button asChild className="w-full rounded-2xl h-14 bg-gray-50 hover:bg-[#FFD700] text-[#0A1A2F] border-none transition-colors">
                  <Link to="/programs">Explore Pathway</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose ASTRA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative max-w-lg mx-auto lg:mx-0">
              <div className="bg-gradient-to-br from-[#2E5C8A] to-[#5B9BD5] rounded-[48px] p-1.5 aspect-square relative overflow-hidden">
                <div className="w-full h-full bg-white rounded-[46px] overflow-hidden">
                  <ImageWithFallback 
                    // src="https://images.unsplash.com/photo-1603354351149-e97b9124020d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyb2JvdGljcyUyMGtpdCUyMGZvciUyMGNoaWxkcmVufGVufDF8fHx8MTc3NjkzMzA4N3ww" 
                    src="/Whisk_boy_casual_.png"
                    // src="https://labs.google/fx/tools/whisk/share/6mp0g5ds40000"
                    alt="Why ASTRA"
                    className="w-full h-full object-cover grayscale-0"
                  />
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#FFD700] p-6 rounded-[32px] shadow-2xl max-w-[200px]">
                <div className="text-3xl font-extrabold text-[#0A1A2F] mb-1">15+</div>
                <div className="text-sm font-bold text-[#2E5C8A] leading-tight">Years of Excellence in STEM</div>
              </div>
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl text-[#0A1A2F] mb-8 tracking-tight">
                Why Parents and Schools <br />
                <span className="text-[#5B9BD5]">Trust Astra</span>
              </h2>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#E0F7FA] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-[#00BCD4]" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-[#0A1A2F] mb-1">Certified Educators</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">Our instructors are trained educators who know how to engage and inspire young minds.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#FFF9C4] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-[#FBC02D]" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-[#0A1A2F] mb-1">Outcome-Based Learning</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">Students build real apps, robots, and games that they can showcase in their portfolios.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#E3F2FD] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-[#2196F3]" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-[#0A1A2F] mb-1">Global Competitions</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">We prepare students for international robotics and coding competitions.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-[#2E5C8A] via-[#5B9BD5] to-[#2E5C8A] rounded-[48px] p-10 md:p-16 text-center relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-white/5 skew-x-[-30deg] translate-x-1/2" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl text-white mb-6 tracking-tight">
                Empower Your Child <br />For the Tech-Driven World
              </h2>
              <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed">
                Join our next cohort and watch your child transform into a confident problem solver and innovator.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button className="bg-[#FFD700] hover:bg-[#FFC700] text-[#0A1A2F] h-14 px-10 rounded-full text-lg font-extrabold shadow-xl transition-all hover:scale-105 active:scale-95">
                  Start Your Free Trial
                </Button>
                <Button variant="outline" className="border-2 border-white/50 hover:bg-white/10 text-white h-14 px-10 rounded-full text-lg font-extrabold transition-all hover:scale-105 active:scale-95">
                  Book a Consultation
                </Button>
              </div>
              <div className="mt-12 flex flex-wrap justify-center gap-8 text-white/60 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#00FFFF] rounded-full" />
                  No Credit Card Required
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#FFD700] rounded-full" />
                  Ages 5-18 Programs
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  Expert Live Instruction
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
